// src/App.js
import { useEffect, useState } from 'react';

import HotelCheckin from './HotelCheckin';
import ChefDashboard from './ChefDashboard';
import KitchenDashboard from './KitchenDashboard';
import AdminActAs from './AdminActAs';
import MemberProfile from './MemberProfile';
import AdminMessages from './AdminMessages';
import AdminMembers from './AdminMembers';
import VotePage from './VotePage';
import VoteTally from './VoteTally';
import AdminBallots from './AdminBallots';
import AccountingDashboard from './AccountingDashboard';
import MenuKiosk from './MenuKiosk';

import users from './users';
import { API_BASE, sheetUrl, byIdUrl, searchUrl, todayCST } from './config';
import { isMealPastCutoff } from './specialDates';
import { useTheme, tokens, ThemeToggleButton } from './theme';
import { useDebug } from './debug';

export default function App() {
  const [theme, setTheme] = useTheme();
  const t = tokens(theme);
  const [DEBUG_ENABLED] = useDebug();

  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [adminView, setAdminView] = useState('actas');
  // 'actas' | 'messages' | 'ballots' | 'tally' | 'members'

  const [memberView, setMemberView] = useState('checkin'); // checkin | profile | menu | messages | vote
  const [futureMeals, setFutureMeals] = useState([]);

  const [menuByDate, setMenuByDate] = useState({}); // { 'YYYY-MM-DD': { breakfast,lunch,dinner,notes } }
  const [messages, setMessages] = useState([]);

  // Debug state (kept, but UI is hidden unless DEBUG_ENABLED is true)
  const [showDebug, setShowDebug] = useState(false);
  const [debug, setDebug] = useState({
    lastRun: '',
    error: '',
    bookingsOk: false,
    menuOk: false,
    messagesOk: false,
    bookingsCount: 0,
    menuCount: 0,
    messagesCount: 0,
    matchedCount: 0,
    memberKey: '',
    bookingsSample: null,
    menuSample: null,
    messagesSample: null,
  });

  // ===== Data load: bookings + menu + messages =====
  const fetchAllForMember = async (memberNumber) => {
    const memberKey = String(memberNumber ?? '').trim();
    const today = todayCST();

    try {
      // 1) BOOKINGS: Sheet1
      const bookingsRes = await fetch(sheetUrl('Sheet1'), { cache: 'no-store' });
      const bookingsOk = bookingsRes.ok;
      const bookingsData = bookingsOk ? await bookingsRes.json() : [];

      const matched = (bookingsData || []).filter((row) => {
        const rowMember = String(
          row.memberNumber ?? row.membernumber ?? row['Member Number'] ?? ''
        ).trim();
        return rowMember === memberKey;
      });

      const upcoming = matched.filter((row) => String(row.date || '').trim() >= today);
      setFutureMeals(upcoming);

      // 2) MENU: Menu sheet
      const menuRes = await fetch(sheetUrl('Menu'), { cache: 'no-store' });
      const menuOk = menuRes.ok;
      const menuData = menuOk ? await menuRes.json() : [];

      const menuMap = {};
      (menuData || []).forEach((row) => {
        const d = String(row.date || '').trim();
        if (!d) return;
        menuMap[d] = {
          breakfast: row.breakfast || '',
          lunch: row.lunch || '',
          dinner: row.dinner || '',
          notes: row.chefs_note || '',
        };
      });
      setMenuByDate(menuMap);

      // 3) MESSAGES: Messages sheet
      const msgRes = await fetch(sheetUrl('Messages'), { cache: 'no-store' });
      const messagesOk = msgRes.ok;
      const msgData = messagesOk ? await msgRes.json() : [];

      const msgs = (msgData || [])
        .filter((row) => row.body && String(row.body).trim() !== '')
        .filter((row) => {
          const start = String(row.startDate || '').trim();
          const end = String(row.endDate || '').trim();
          if (start && today < start) return false;
          if (end && today > end) return false;
          return true;
        })
        .map((row) => ({
          id:
            String(row.id || '').trim() ||
            `${row.timestamp || ''}_${row.title || ''}`.trim() ||
            `msg_${Math.random().toString(16).slice(2)}`,
          title: row.title || 'Message',
          body: row.body,
          startDate: row.startDate || '',
          endDate: row.endDate || '',
          audience: row.audience || 'all',
          timestamp: row.timestamp || '',
        }))
        .sort((a, b) => {
          const ta = a.timestamp ? Date.parse(a.timestamp) : 0;
          const tb = b.timestamp ? Date.parse(b.timestamp) : 0;
          return tb - ta;
        });

      setMessages(msgs);

      if (DEBUG_ENABLED) {
        setDebug({
          lastRun: new Date().toLocaleString(),
          error: '',
          bookingsOk,
          menuOk,
          messagesOk,
          bookingsCount: (bookingsData || []).length,
          menuCount: (menuData || []).length,
          messagesCount: (msgData || []).length,
          matchedCount: matched.length,
          memberKey,
          bookingsSample: (bookingsData || [])[0] || null,
          menuSample: (menuData || [])[0] || null,
          messagesSample: (msgData || [])[0] || null,
        });
      }
    } catch (err) {
      console.error('fetchAllForMember failed', err);
      setFutureMeals([]);
      setMenuByDate({});
      setMessages([]);

      if (DEBUG_ENABLED) {
        setDebug((d) => ({
          ...d,
          lastRun: new Date().toLocaleString(),
          error: String(err),
        }));
      }
    }
  };

  // ===== Toggle a single meal (member-side), respecting cutoff =====
  async function toggleMyMeal(row, meal) {
    const currentYes = String(row?.[meal] || '').toLowerCase() === 'yes';
    const rowDate = String(row?.date || '').trim();

    if (!row?.id) {
      alert('Missing row id; cannot update.');
      return;
    }
    if (!rowDate) {
      alert('Missing row date; cannot update.');
      return;
    }

    if (isMealPastCutoff(meal, rowDate)) {
      alert(`${meal} cutoff passed for ${rowDate}.`);
      return;
    }

    try {
      const res = await fetch(byIdUrl('Sheet1', row.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { [meal]: currentYes ? '' : 'Yes' } }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `PATCH failed (${res.status})`);
      }

      await fetchAllForMember(authenticatedUser.memberNumber);
    } catch (e) {
      console.error('Failed to update meal', e);
      alert('Failed to update meal.');
    }
  }

  // ===== Delete a whole row =====
  const deleteBooking = async (row) => {
    if (!row?.id) return;

    if (!window.confirm(`Are you sure you want to delete ${row.name} on ${row.date}?`)) return;

    try {
      const res = await fetch(byIdUrl('Sheet1', row.id), {
        method: 'DELETE',
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `DELETE failed (${res.status})`);
      }

      await fetchAllForMember(authenticatedUser.memberNumber);
    } catch (err) {
      console.error('Error deleting booking', err);
      alert('Failed to delete booking.');
    }
  };

  // Pull the latest `role` column from the Members sheet for this user and
  // augment the in-memory user with `isBoard` accordingly. Other role flags
  // (isAdmin, isChef, etc.) still come from users.jsx — the routing-level
  // roles need to match the PIN file. The Members.role column drives the
  // augmentation-only flag isBoard so admins can manage board membership
  // without code edits.
  const enrichRoleFromMembers = async (user) => {
    if (!user || !user.memberNumber) return user;
    try {
      const res = await fetch(searchUrl('Members', { memberNumber: user.memberNumber }), { cache: 'no-store' });
      if (!res.ok) return user;
      const rows = await res.json();
      const row = (rows || [])[0];
      const role = String(row?.role || '').trim().toLowerCase();
      return { ...user, isBoard: role === 'board' };
    } catch (e) {
      console.warn('App: Members.role lookup failed', e);
      return user;
    }
  };

  // ===== Auth / init =====
  useEffect(() => {
    const saved = localStorage.getItem('authenticatedUser');
    if (saved) {
      const user = JSON.parse(saved);
      setAuthenticatedUser(user);
      setMemberView('checkin');
      fetchAllForMember(user.memberNumber);
      // Refresh board status from the Members sheet on every page load so
      // a yearly board rotation takes effect without re-login.
      enrichRoleFromMembers(user).then((enriched) => {
        if (enriched.isBoard !== user.isBoard) {
          localStorage.setItem('authenticatedUser', JSON.stringify(enriched));
          setAuthenticatedUser(enriched);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async () => {
    const user = users[pinInput];
    if (!user) {
      alert('Incorrect PIN');
      return;
    }
    const enriched = await enrichRoleFromMembers(user);
    localStorage.setItem('authenticatedUser', JSON.stringify(enriched));
    setAuthenticatedUser(enriched);
    setMemberView('checkin');
    fetchAllForMember(enriched.memberNumber);
  };

  const onLogout = () => {
    localStorage.removeItem('authenticatedUser');
    setAuthenticatedUser(null);
    setMemberView('checkin');
    setFutureMeals([]);
    setMenuByDate({});
    setMessages([]);

    if (DEBUG_ENABLED) {
      setShowDebug(false);
      setDebug({
        lastRun: '',
        error: '',
        bookingsOk: false,
        menuOk: false,
        messagesOk: false,
        bookingsCount: 0,
        menuCount: 0,
        messagesCount: 0,
        matchedCount: 0,
        memberKey: '',
        bookingsSample: null,
        menuSample: null,
        messagesSample: null,
      });
    }
  };

  // ===== ROUTING: Chef/Kitchen/Accounting/Kiosk/Admin =====
  if (authenticatedUser?.isChef) return <ChefDashboard />;
  if (authenticatedUser?.isKitchen) return <KitchenDashboard />;
  if (authenticatedUser?.isAccountant) return <AccountingDashboard />;
  if (authenticatedUser?.isKiosk) return <MenuKiosk />;
  if (authenticatedUser?.isAdmin) {
    return (
      <div style={{ background: t.page, color: t.text, minHeight: '100vh' }}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <AdminTab
            label="👤 Act As Member"
            active={adminView === 'actas'}
            onClick={() => setAdminView('actas')}
            t={t}
          />
          <AdminTab
            label="📣 Messages"
            active={adminView === 'messages'}
            onClick={() => setAdminView('messages')}
            t={t}
          />
          <AdminTab
            label="🗳️ Ballots"
            active={adminView === 'ballots'}
            onClick={() => setAdminView('ballots')}
            t={t}
          />
          <AdminTab
            label="📊 Vote Tally"
            active={adminView === 'tally'}
            onClick={() => setAdminView('tally')}
            t={t}
          />
          <AdminTab
            label="👥 Members"
            active={adminView === 'members'}
            onClick={() => setAdminView('members')}
            t={t}
          />

          <ThemeToggleButton theme={theme} setTheme={setTheme} style={{ marginLeft: 'auto' }} />

          <button
            onClick={onLogout}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: '1px solid #b91c1c',
              background: '#991b1b',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            🔒 Logout
          </button>
        </div>

        {adminView === 'actas' && <AdminActAs users={users} />}
        {adminView === 'messages' && <AdminMessages />}
        {adminView === 'ballots' && <AdminBallots />}
        {adminView === 'tally' && <VoteTally />}
        {adminView === 'members' && <AdminMembers authenticatedUser={authenticatedUser} />}
      </div>
    );
  }

  if (!authenticatedUser) {
    return (
      <div
        style={{
          padding: '40px',
          fontFamily: 'Arial',
          textAlign: 'center',
          backgroundImage: 'url("/background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          color: 'white',
          textShadow: '0 0 5px black',
        }}
      >
        <div
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: '20px',
            borderRadius: '8px',
            display: 'inline-block',
          }}
        >
          <img
            src="/WOC-Icon.png"
            alt="Wauhillau Logo"
            style={{ maxWidth: '150px', marginBottom: '20px' }}
          />
          <h2>Member PIN</h2>

          <input
            type="password"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLogin();
            }}
            style={{ fontSize: '18px', padding: '6px' }}
          />
          <br />

          <button onClick={handleLogin} style={{ marginTop: '10px', padding: '8px 16px' }}>
            Submit
          </button>

          {DEBUG_ENABLED && (
            <div style={{ marginTop: 12, fontSize: 11, opacity: 0.75 }}>
              API: {API_BASE}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial', background: t.page, color: t.text, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: t.textStrong }}>Welcome, {authenticatedUser.name}!</h2>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {DEBUG_ENABLED && (
            <button
              onClick={() => setShowDebug((s) => !s)}
              style={{
                padding: '6px 10px',
                border: `1px solid ${t.inputBorder}`,
                background: showDebug ? '#fff3cd' : t.inputBg,
                color: showDebug ? '#92400e' : t.text,
                cursor: 'pointer',
                borderRadius: 6,
              }}
              title="Shows counts + sample rows to diagnose SheetDB fetching"
            >
              🧪 Debug
            </button>
          )}

          <ThemeToggleButton theme={theme} setTheme={setTheme} />

          <button
            onClick={onLogout}
            style={{
              padding: '6px 12px',
              backgroundColor: '#555',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 6,
            }}
          >
            🔒 Logout
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          onClick={() => setMemberView('checkin')}
          style={{
            padding: '10px 20px',
            fontSize: 18,
            borderRadius: 8,
            border: `1px solid ${t.inputBorder}`,
            background: memberView === 'checkin' ? t.pillOnBg : t.inputBg,
            color: memberView === 'checkin' ? t.pillOnFg : t.text,
            cursor: 'pointer',
            width: '100%',
            maxWidth: 300,
          }}
        >
          ✅ Check-In & Book Meals
        </button>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <TabButton label="👤 Member Profile" active={memberView === 'profile'} onClick={() => setMemberView('profile')} t={t} />
        <TabButton label="🍽️ Menu" active={memberView === 'menu'} onClick={() => setMemberView('menu')} t={t} />
        <TabButton label="💬 Messages" active={memberView === 'messages'} onClick={() => setMemberView('messages')} t={t} />
        <TabButton label="🗳️ Vote" active={memberView === 'vote'} onClick={() => setMemberView('vote')} t={t} />
      </div>

      {DEBUG_ENABLED && showDebug && (
        <div style={{ marginTop: 12, padding: 10, border: '1px dashed #999', maxWidth: 1100 }}>
          <div style={{ fontWeight: 'bold' }}>Data Debug</div>
          <div>Last run: {debug.lastRun}</div>
          {debug.error && <div style={{ color: 'red' }}>Error: {debug.error}</div>}
          <div>MemberKey: {debug.memberKey}</div>
          <div>
            Bookings OK: {String(debug.bookingsOk)} | rows: {debug.bookingsCount} | matched member rows: {debug.matchedCount}
          </div>
          <div>Menu OK: {String(debug.menuOk)} | rows: {debug.menuCount}</div>
          <div>Messages OK: {String(debug.messagesOk)} | rows: {debug.messagesCount}</div>

          <div style={{ marginTop: 6, fontSize: 12, color: '#555', wordBreak: 'break-word' }}>
            Sample booking row: {debug.bookingsSample ? JSON.stringify(debug.bookingsSample) : '(none)'}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#555', wordBreak: 'break-word' }}>
            Sample menu row: {debug.menuSample ? JSON.stringify(debug.menuSample) : '(none)'}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#555', wordBreak: 'break-word' }}>
            Sample message row: {debug.messagesSample ? JSON.stringify(debug.messagesSample) : '(none)'}
          </div>

          <button
            onClick={() => fetchAllForMember(authenticatedUser.memberNumber)}
            style={{ marginTop: 10, padding: '6px 10px', cursor: 'pointer' }}
          >
            🔄 Force Refresh
          </button>
        </div>
      )}

      {(() => {
        const boxStyle = { border: `1px solid ${t.border}`, borderRadius: 8, padding: 12, maxWidth: 700, background: t.card, color: t.text };
        const itemStyle = { marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${t.border}` };
        const bookingStyle = { marginBottom: '10px', paddingBottom: '10px', borderBottom: `1px solid ${t.border}` };
        const mealButtonsRowStyle = { marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' };

        return (
      <div style={{ marginTop: 16 }}>
        {memberView === 'profile' && <MemberProfile authenticatedUser={authenticatedUser} />}

        {memberView === 'menu' && (
          <div style={boxStyle}>
            <h3 style={{ marginTop: 0, color: t.textStrong }}>Menu</h3>

            {futureMeals.length === 0 ? (
              <p style={{ fontSize: 13, color: t.muted }}>
                No future bookings found for your member number ({String(authenticatedUser.memberNumber)}).
              </p>
            ) : (
              (() => {
                const uniqueDates = [...new Set(futureMeals.map((r) => String(r.date || '').trim()))].filter(Boolean);
                return uniqueDates.map((date) => {
                  const menu = menuByDate[date];
                  return (
                    <div key={date} style={itemStyle}>
                      <div style={{ fontWeight: 'bold', color: t.textStrong }}>{date}</div>
                      {!menu ? (
                        <div style={{ fontSize: 13, color: t.muted }}>Menu not set yet for this date.</div>
                      ) : (
                        <div style={{ fontSize: 13 }}>
                          {menu.breakfast && <div>Breakfast: {menu.breakfast}</div>}
                          {menu.lunch && <div>Lunch: {menu.lunch}</div>}
                          {menu.dinner && <div>Dinner: {menu.dinner}</div>}
                          {menu.notes && <div>Notes: {menu.notes}</div>}
                        </div>
                      )}
                    </div>
                  );
                });
              })()
            )}
          </div>
        )}

        {memberView === 'messages' && (() => {
          // Audience gate: hide 'board' messages from non-board users.
          // 'members' and 'all' show to anyone authenticated; admins see everything.
          const isBoard = !!authenticatedUser?.isBoard;
          const isAdmin = !!authenticatedUser?.isAdmin;
          const visibleMessages = messages.filter((msg) => {
            const aud = (msg.audience || '').toString().trim().toLowerCase();
            if (aud === 'board' || aud === 'board only' || aud === 'board-only') {
              return isBoard || isAdmin;
            }
            return true;
          });

          return (
            <div style={boxStyle}>
              <h3 style={{ marginTop: 0, color: t.textStrong }}>Messages</h3>

              {visibleMessages.length === 0 ? (
                <p style={{ fontSize: 13, color: t.muted }}>No messages at this time.</p>
              ) : (
                visibleMessages.map((msg) => (
                  <div key={msg.id} style={itemStyle}>
                    <div style={{ fontWeight: 'bold', color: t.textStrong }}>{msg.title}</div>
                    <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                    {(msg.startDate || msg.endDate || msg.audience) && (
                      <div style={{ fontSize: 11, color: t.muted, marginTop: 3 }}>
                        {msg.startDate ? `From: ${msg.startDate}` : ''}
                        {msg.endDate ? `  Until: ${msg.endDate}` : ''}
                        {msg.audience ? `  Audience: ${msg.audience}` : ''}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          );
        })()}

        {memberView === 'vote' && <VotePage authenticatedUser={authenticatedUser} />}

        {memberView === 'checkin' && (
          <>
            <h3 style={{ marginTop: 12, color: t.textStrong }}>Future Bookings</h3>

            {futureMeals.length === 0 ? (
              <p style={{ fontSize: 13, color: t.muted }}>
                No future bookings found for your member number ({String(authenticatedUser.memberNumber)}).
                {DEBUG_ENABLED && (
                  <>
                    <br />
                    (Turn on 🧪 Debug to see what SheetDB is returning.)
                  </>
                )}
              </p>
            ) : (
              futureMeals.map((row, idx) => {
                const rowDate = String(row.date || '').trim();

                const bookedMeals = ['breakfast', 'lunch', 'dinner'].filter(
                  (meal) => String(row?.[meal] || '').toLowerCase() === 'yes'
                );

                const allBookedLocked =
                  bookedMeals.length > 0 && bookedMeals.every((meal) => isMealPastCutoff(meal, rowDate));

                return (
                  <div key={row.id || idx} style={bookingStyle}>
                    <div>
                      📅 {rowDate} — {row.name}: {bookedMeals.join(', ') || 'No meals booked'}
                    </div>

                    <div style={mealButtonsRowStyle}>
                      {['breakfast', 'lunch', 'dinner'].map((meal) => {
                        const isYes = String(row?.[meal] || '').toLowerCase() === 'yes';
                        const locked = isMealPastCutoff(meal, rowDate);

                        return (
                          <button
                            key={meal}
                            onClick={() => toggleMyMeal(row, meal)}
                            disabled={locked}
                            style={{
                              padding: '4px 10px',
                              opacity: locked ? 0.5 : 1,
                              border: `1px solid ${isYes ? t.danger : t.ok}`,
                              background: isYes ? (theme === 'dark' ? 'rgba(239,68,68,0.15)' : '#fff6f6') : (theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#f6fff6'),
                              color: isYes ? t.danger : t.pillOnFg,
                              cursor: locked ? 'not-allowed' : 'pointer',
                              textTransform: 'capitalize',
                              borderRadius: 6,
                            }}
                            title={locked ? `${meal} cutoff passed` : ''}
                          >
                            {isYes ? `Cancel ${meal}` : `Add ${meal}`}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => deleteBooking(row)}
                      disabled={allBookedLocked}
                      style={{
                        marginTop: 6,
                        padding: '4px 12px',
                        color: t.danger,
                        background: 'transparent',
                        border: `1px solid ${t.danger}`,
                        borderRadius: 6,
                        cursor: allBookedLocked ? 'not-allowed' : 'pointer',
                        opacity: allBookedLocked ? 0.5 : 1,
                      }}
                    >
                      🗑️ Delete entire day
                    </button>

                    {allBookedLocked && <span style={{ color: t.muted, marginLeft: 6 }}>(All booked meals locked)</span>}
                  </div>
                );
              })
            )}

            <HotelCheckin
              memberNumber={authenticatedUser.memberNumber}
              onSubmitted={() => fetchAllForMember(authenticatedUser.memberNumber)}
            />
          </>
        )}
      </div>
        );
      })()}
    </div>
  );
}

function TabButton({ label, active, onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 6,
        border: `1px solid ${t.inputBorder}`,
        background: active ? t.accentSoft : t.inputBg,
        color: active ? t.accent : t.text,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function AdminTab({ label, active, onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 14px',
        borderRadius: 10,
        border: `1px solid ${active ? t.accent : t.inputBorder}`,
        background: active ? t.accent : t.inputBg,
        color: active ? 'white' : t.text,
        cursor: 'pointer',
        fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}
